/**
 * CollisionGroups - Unified collision layer definitions
 * 
 * These bit flags are used for physics collision filtering.
 * Objects can belong to one or more groups and filter which groups they collide with.
 * 
 * @example
 * ```typescript
 * // Player collides with terrain and obstacles
 * const playerMask = CollisionGroup.TERRAIN | CollisionGroup.OBSTACLE;
 * 
 * // Check if two objects should collide
 * const shouldCollide = (groupA & maskB) !== 0 && (groupB & maskA) !== 0;
 * ```
 */

/**
 * Collision group bit flags
 * Use powers of 2 for bitwise operations
 */
export enum CollisionGroup {
    /** No collision */
    NONE = 0x0000,
    
    /** Default collision group */
    DEFAULT = 0x0001,
    
    /** Terrain/ground surfaces */
    TERRAIN = 0x0002,
    
    /** Player-controlled entities */
    PLAYER = 0x0004,
    
    /** Non-player characters and creatures */
    NPC = 0x0008,
    
    /** Static environment objects (trees, rocks, buildings) */
    STATIC = 0x0010,
    
    /** Dynamic/moveable objects */
    DYNAMIC = 0x0020,
    
    /** Projectiles and throwables */
    PROJECTILE = 0x0040,
    
    /** Trigger volumes (non-solid) */
    TRIGGER = 0x0080,
    
    /** Water volumes */
    WATER = 0x0100,
    
    /** Interactable objects */
    INTERACTABLE = 0x0200,
    
    /** Vehicles */
    VEHICLE = 0x0400,
    
    /** Debris/particles (often ignored) */
    DEBRIS = 0x0800,
    
    /** Sensors/raycasts only */
    SENSOR = 0x1000,

    /** All groups combined */
    ALL = 0xFFFF,
}

/**
 * Common collision mask presets
 * These define what groups an object should collide WITH
 */
export const CollisionMask = {
    /** Collides with everything */
    ALL: CollisionGroup.ALL,
    
    /** Collides with nothing */
    NONE: CollisionGroup.NONE,
    
    /** Standard solid object (terrain, static, dynamic) */
    SOLID: CollisionGroup.TERRAIN | CollisionGroup.STATIC | CollisionGroup.DYNAMIC,
    
    /** Character collision (terrain, static, dynamic, other characters) */
    CHARACTER: CollisionGroup.TERRAIN | CollisionGroup.STATIC | CollisionGroup.DYNAMIC | 
               CollisionGroup.PLAYER | CollisionGroup.NPC,
    
    /** Projectile collision (terrain, static, characters) */
    PROJECTILE: CollisionGroup.TERRAIN | CollisionGroup.STATIC | 
                CollisionGroup.PLAYER | CollisionGroup.NPC,
    
    /** Water interaction (player, NPC, dynamic) */
    WATER: CollisionGroup.PLAYER | CollisionGroup.NPC | CollisionGroup.DYNAMIC,
    
    /** Trigger zones (player, NPC) */
    TRIGGER: CollisionGroup.PLAYER | CollisionGroup.NPC,
} as const;

/**
 * Collision preset configuration
 */
export interface CollisionPreset {
    /** The group(s) this object belongs to */
    group: CollisionGroup;
    /** The group(s) this object collides with */
    mask: number;
}

/**
 * Pre-defined collision presets for common object types
 */
export const CollisionPresets: Record<string, CollisionPreset> = {
    /** Ground/terrain preset */
    terrain: {
        group: CollisionGroup.TERRAIN,
        mask: CollisionMask.ALL,
    },
    
    /** Player character preset */
    player: {
        group: CollisionGroup.PLAYER,
        mask: CollisionMask.CHARACTER,
    },
    
    /** NPC/creature preset */
    npc: {
        group: CollisionGroup.NPC,
        mask: CollisionMask.CHARACTER,
    },
    
    /** Static environment object (tree, rock, building) */
    static: {
        group: CollisionGroup.STATIC,
        mask: CollisionMask.SOLID | CollisionGroup.PLAYER | CollisionGroup.NPC,
    },
    
    /** Dynamic/physics object */
    dynamic: {
        group: CollisionGroup.DYNAMIC,
        mask: CollisionMask.ALL & ~CollisionGroup.TRIGGER & ~CollisionGroup.SENSOR,
    },
    
    /** Projectile preset */
    projectile: {
        group: CollisionGroup.PROJECTILE,
        mask: CollisionMask.PROJECTILE,
    },
    
    /** Trigger volume (non-solid) */
    trigger: {
        group: CollisionGroup.TRIGGER,
        mask: CollisionMask.TRIGGER,
    },
    
    /** Water volume */
    water: {
        group: CollisionGroup.WATER,
        mask: CollisionMask.WATER,
    },
    
    /** Interactable object */
    interactable: {
        group: CollisionGroup.INTERACTABLE | CollisionGroup.STATIC,
        mask: CollisionGroup.PLAYER | CollisionGroup.TERRAIN,
    },
};

/**
 * Helper to check if two objects with given groups/masks should collide
 */
export function shouldCollide(
    groupA: CollisionGroup,
    maskA: number,
    groupB: CollisionGroup,
    maskB: number
): boolean {
    return (groupA & maskB) !== 0 && (groupB & maskA) !== 0;
}

/**
 * Helper to create a combined group from multiple groups
 */
export function combineGroups(...groups: CollisionGroup[]): number {
    return groups.reduce((combined, group) => combined | group, CollisionGroup.NONE);
}
